import { useEffect, useState } from 'react';
import type { CardInfo } from '@lub0v/santa-utils';

export interface ParticipantPage {
  progressParticipants?: import('../ParticipantProgress').ProgressParticipant[];
  items: CardInfo[];
  related: CardInfo[];
  total: number;
  page: number;
  pageSize: number;
  summary: { total: number; named: number; unassigned: number; exclusions: number; invited: number };
}

export function useOrganizerParticipantPage(key: string, enabled: boolean, request: {
  page: number; pageSize: number; search: string; filters: string; sort: string; progress?: string;
}, revision: unknown, append = false) {
  const [result, setResult] = useState<{ key: string; group: string; data: ParticipantPage } | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [cache, setCache] = useState(() => ({ key, revision, cards: new Map<number, CardInfo>() }));
  const group = JSON.stringify([key, request.search, request.filters, request.sort, request.progress, request.pageSize]);
  const query = new URLSearchParams(Object.entries(request).map(([name, value]) => [name, String(value)])).toString();
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset loading and error state when starting a new external request.
    setLoading(true);
    setError(false);
    // Debounce search changes and cancel superseded requests (including page-size changes).
    const timer = window.setTimeout(() => {
      void fetch(`/api/box/${encodeURIComponent(key)}/participants?${query}`, { signal: controller.signal })
        .then(async response => {
          if (!response.ok) throw new Error('Participant page unavailable');
          const data: ParticipantPage = await response.json();
          if (controller.signal.aborted) return;
          setCache(previous => {
            const cards = new Map(previous.key === key && previous.revision === revision ? previous.cards : []);
            for (const card of [...data.items, ...data.related]) cards.set(card.id, card);
            return { key, revision, cards };
          });
          setResult(previous => ({ key, group, data: append && request.page > 1 && previous?.group === group
            ? { ...data, items: [...new Map([...previous.data.items, ...data.items].map(card => [card.id, card])).values()] }
            : data }));
          setLoading(false);
        }).catch(() => { if (!controller.signal.aborted) { setError(true); setLoading(false); } });
    }, 150);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [key, enabled, query, revision, append]);
  return { data: result?.key === key && result.group === group ? result.data : null, error, loading, cachedCards: cache.key === key && cache.revision === revision ? [...cache.cards.values()] : [] };
}
